import {
  useGetPartnerLogoQuery,
  usePostPartnerLogoMutation,
} from '@/api/api.slice.tsx';
import { PartnerType } from '@/api/types.ts';
import { Field } from '@/features/PartnerInfo/fields/Field.tsx';
import { useUpdatePartner } from '@/features/PartnerInfo/useUpdatePartner.ts';
import {
  Alert,
  Button,
  Collapse,
  Editable,
  EditableEmailTextField,
  EditableTextField,
  getMessageFromError,
  Skeleton,
  Uploader,
} from '@arsnovasystems/ars-front-lib';
import CloudUploadRoundedIcon from '@mui/icons-material/CloudUploadRounded';
import { Box, Stack } from '@mui/material';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

/**
 * CompanyFields
 *
 * @param props    component properties
 * @param props.me the connected partner
 *
 * @returns        JSX of the component
 */
export const CompanyFields = ({ me }: { me: PartnerType }) => {
  const { t } = useTranslation();
  const [isUploaderShown, setIsUploaderShown] = useState(false);
  const { savePartner, error } = useUpdatePartner();
  const { data, isLoading, refetch, isFetching } = useGetPartnerLogoQuery();
  const [saveLogoMutation, saveLogoMutationResult] =
    usePostPartnerLogoMutation();
  const handleUploadLogo = async (file: File) => {
    const result = await saveLogoMutation(file);
    if (result.error === undefined) setIsUploaderShown(false);
    void refetch();
  };

  return (
    <Stack
      sx={{
        marginTop: 3,
      }}
    >
      <Box
        sx={(theme) => ({
          width: 'fit-content',
          padding: 1,
          borderRadius: 1,
          border: `1px solid ${theme.palette.border.light}`,
        })}
      >
        {isLoading || isFetching ?
          <Skeleton
            variant='rounded'
            width={200}
            height={200}
          />
        : <>
            {data ?
              <Box
                sx={{
                  width: 200,
                  height: 200,
                }}
              >
                <img
                  style={{
                    objectFit: 'contain',
                  }}
                  src={data}
                  height={200}
                />
              </Box>
            : null}
          </>
        }
      </Box>

      <Button
        onClick={() => {
          setIsUploaderShown((curr) => !curr);
        }}
        startIcon={<CloudUploadRoundedIcon />}
        sx={{
          marginTop: 2,
          width: 'fit-content',
        }}
      >
        {t('Modify')}
      </Button>

      <Collapse isExpanded={isUploaderShown}>
        <Uploader
          maxFiles={1}
          accept={{
            'image/jpeg': [],
            'image/png': [],
          }}
          disablePreview
          onChange={(files) => {
            if (files[0]) {
              void handleUploadLogo(files[0]);
            }
          }}
          sx={{
            marginTop: 1,
            maxWidth: 400,
          }}
        />
      </Collapse>

      <Field
        sx={{
          marginTop: 2,
        }}
        label={t('ik.company.name')}
        EditablePart={
          <Editable
            label={me.name}
            render={({ close }) => {
              return (
                <>
                  <EditableTextField
                    required
                    label={t('ik.company.name')}
                    defaultValue={me.name}
                    onChange={async (updatedCompanyName) => {
                      const result = await savePartner({
                        name: updatedCompanyName,
                      });
                      if (result.error === undefined) {
                        close();
                      }
                    }}
                  />
                </>
              );
            }}
          />
        }
      />
      <Field
        label={t('ik.accounting.email')}
        EditablePart={
          <Editable
            label={me.accountingEmail}
            render={({ close }) => {
              return (
                <>
                  <EditableEmailTextField
                    label={t('ik.accounting.email')}
                    defaultValue={me.accountingEmail}
                    onChange={async (updatedAccountingEmail) => {
                      const result = await savePartner({
                        accountingEmail: updatedAccountingEmail,
                      });
                      if (result.error === undefined) {
                        close();
                      }
                    }}
                  />
                </>
              );
            }}
          />
        }
      />
      <Field
        label={t('ik.vat')}
        EditablePart={
          <Editable
            label={me.companyVatNumber}
            render={({ close }) => {
              return (
                <>
                  <EditableTextField
                    required
                    label={t('ik.vat')}
                    defaultValue={me.companyVatNumber}
                    onChange={async (updatedString) => {
                      const result = await savePartner({
                        companyVatNumber: updatedString,
                      });
                      if (result.error === undefined) {
                        close();
                      }
                    }}
                  />
                </>
              );
            }}
          />
        }
      />
      <Field
        label={t('ik.adresse')}
        EditablePart={
          <Editable
            label={me.address}
            render={({ close }) => {
              return (
                <>
                  <EditableTextField
                    label={t('ik.adresse')}
                    required
                    defaultValue={me.address}
                    onChange={async (updatedString) => {
                      const result = await savePartner({
                        address: updatedString,
                      });
                      if (result.error === undefined) {
                        close();
                      }
                    }}
                  />
                </>
              );
            }}
          />
        }
      />
      <Field
        label={t('ik.state')}
        EditablePart={
          <Editable
            label={me.stateRegion}
            render={({ close }) => {
              return (
                <>
                  <EditableTextField
                    required
                    label={t('ik.state')}
                    defaultValue={me.stateRegion}
                    onChange={async (updatedString) => {
                      const result = await savePartner({
                        stateRegion: updatedString,
                      });
                      if (result.error === undefined) {
                        close();
                      }
                    }}
                  />
                </>
              );
            }}
          />
        }
      />
      <Field
        label={t('ik.zip.code')}
        EditablePart={
          <Editable
            label={me.zipCode}
            render={({ close }) => {
              return (
                <>
                  <EditableTextField
                    required
                    label={t('ik.zip.code')}
                    defaultValue={me.zipCode}
                    onChange={async (updatedString) => {
                      const result = await savePartner({
                        zipCode: updatedString,
                      });
                      if (result.error === undefined) {
                        close();
                      }
                    }}
                  />
                </>
              );
            }}
          />
        }
      />
      <Field
        label={t('ik.town')}
        EditablePart={
          <Editable
            label={me.town}
            render={({ close }) => {
              return (
                <>
                  <EditableTextField
                    label={t('ik.town')}
                    defaultValue={me.town}
                    required
                    onChange={async (updatedString) => {
                      const result = await savePartner({
                        town: updatedString,
                      });
                      if (result.error === undefined) {
                        close();
                      }
                    }}
                  />
                </>
              );
            }}
          />
        }
      />

      {error ?
        <Alert>{getMessageFromError(error)}</Alert>
      : null}
      {saveLogoMutationResult.error ?
        <Alert>{getMessageFromError(saveLogoMutationResult.error)}</Alert>
      : null}
    </Stack>
  );
};
